// src/components/reports/ReportsPortalMUI.jsx
import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Stack,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  Alert,
  Paper,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  CircularProgress,
  Box,
  Slider,
  Menu,
  MenuItem as MuiMenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import ScrollableTableContainer from "../common/ScrollableTableContainer";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import SearchableDeviceSelect from "../common/SearchableDeviceSelect";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { navigateToAdminDashboard } from "../../router/dashboardNavigation";
import { useSelector } from "react-redux";
import { useWhiteLabelRuntime } from "../../modules/whitelabel/runtime/WhiteLabelRuntimeProvider";
import defaultWebCrmLogo from "../../assets/logo.png";

import { useGetAllLiteDevicesQuery } from "../../redux/admin/adminApi";
import {
  useLazyDownloadReportQuery,
  useLazyGetReportPreviewQuery,
  useLazyGetMoveInSyncPushLogsQuery,
  useLazyDownloadMoveInSyncPushLogsCsvQuery,
  useGetGroupsQuery
} from "../../redux/admin/reportApi";

// Backend should provide addresses. This is only a local fallback formatter.
const getAddressFromCoordinates = async (lat, lon) => {
  const latNum = Number(lat);
  const lonNum = Number(lon);
  if (!Number.isFinite(latNum) || !Number.isFinite(lonNum)) {
    return "No GPS Location";
  }
  return `Lat: ${latNum.toFixed(6)}, Lng: ${lonNum.toFixed(6)}`;
};

// ✅ Format time function
const formatTime = (timestamp) => {
  if (!timestamp) return "N/A";
  
  try {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      // Try to parse as string
      if (typeof timestamp === 'string') {
        return timestamp.substring(0, 20);
      }
      return "Invalid Time";
    }
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    const timeStr = date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    if (date >= today) {
      return `Today ${timeStr}`;
    }
    
    const dateStr = date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short'
    });
    
    return `${dateStr} ${timeStr}`;
  } catch {
    return String(timestamp).substring(0, 20);
  }
};

// ✅ Calculate duration
const calculateDuration = (start, end) => {
  if (!start || !end) return "N/A";
  
  try {
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return "Invalid";
    }
    
    const diffMs = Math.abs(endDate - startDate);
    const minutes = Math.floor(diffMs / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  } catch {
    return "N/A";
  }
};

// ✅ Calculate time gap between consecutive records
const calculateTimeGap = (currentTime, previousTime) => {
  if (!currentTime || !previousTime) return null;
  
  try {
    const current = new Date(currentTime);
    const previous = new Date(previousTime);
    
    if (isNaN(current.getTime()) || isNaN(previous.getTime())) {
      return null;
    }
    
    const gapMinutes = Math.abs(current - previous) / (1000 * 60);
    return gapMinutes;
  } catch {
    return null;
  }
};

// ✅ Backend type mapping (supported + legacy labels)
const BACKEND_TYPE_MAP = {
  "Device Logs Report": "deviceLogs",
  "Device Logs": "deviceLogs",
  "Ignition Report": "ignition",
  "Ignition Reports": "ignition",
  "Engine Report": "summary",
  "Daily Summary Report": "dailySummary",
  "Daily Summary": "dailySummary",
  "Daily Report": "dailySummary",
  "Daily Reports": "dailySummary",
  "Movement Report": "movement",
  "Trip History Report": "trips",
  "Trip Report": "managedTrips",
  "Distance Report": "distance",
  "Distance Reports": "distance",
  "Summary Report": "summary",
  "Geofence Report": "geofence",
  "Geofence Reports": "geofence",
  "Temperature Report": "temperature",
  "Travel Path": "travelPath",
  "Travel Path Report": "travelPath",
  "Travel Path Reports": "travelPath",

  // Legacy dropdown labels - mapped to closest working backend reports
  "Route Report": "route",
  "Stoppage Report": "stoppage",
  "Stoppage Reports": "stoppage",
  "Overspeed Report": "overspeed",
  "Idle Report": "idle",
  "Working Hours": "workingHours",
  "Working Hours Report": "workingHours",
  "Working Hour Report": "workingHours",
  "Events Report": "deviceLogs",
  "Fuel Report": "fuel",
  "Fuel Reports": "fuel",
  "Voltage Report": "voltage",
  "Expense Report": "expense",
  "RFID Report": "rfid",
  "RFID Reports": "rfid",
  "Geofence Trip": "trips",
  "Group Summary": "summary",
  "Summary": "summary",
  "Movement": "movement",
  "Trips": "trips",
  "Distance": "distance",
  "Temperature": "temperature",
  "Ignition": "ignition",
  "AC Report": "acReport",
  "AC Reports": "acReport",
  "ACC / Ignition Summary": "acReport",
  "Chart": "dailySummary",
  "Data Push": "deviceLogs",
};

const REPORT_TYPE_ALIASES = {
  "Device Logs": "deviceLogs",
  "Daily Summary": "dailySummary",
  "Daily Report": "dailySummary",
  "Travel Path": "travelPath",
  "Summary": "summary",
  "Group Summary": "summary",
  "Route Report": "route",
  "Trips": "trips",
  "Movement": "movement",
  "Distance": "distance",
  "Temperature": "temperature",
  "Ignition": "ignition",
  "Working Hours": "workingHours",
  "Working Hours Report": "workingHours",
  "Working Hour Report": "workingHours",
  "Fuel Report": "fuel",
  "Geofence Report": "geofence",
  "RFID Report": "rfid",
  "AC Report": "acReport",
};

const CANONICAL_REPORT_TYPES = [
  "Engine Report",
  "Device Logs Report",
  "Ignition Report",
  "ACC / Ignition Summary",
  "Daily Summary Report",
  "Trip History Report",
  "Trip Report",
  "Movement Report",
  "Distance Report",
  "Stoppage Report",
  "Overspeed Report",
  "Idle Report",
  "Fuel Report",
  "Geofence Report",
  "Temperature Report",
  "Travel Path Report",
  "Working Hours Report",
  "RFID Report",
];

const resolveBackendReportType = (label) =>
  BACKEND_TYPE_MAP[label] || REPORT_TYPE_ALIASES[label] || "deviceLogs";

const TIME_RANGES = [
  "Today",
  "Yesterday",
  "Last 7 Days",
  "Last 30 Days",
  "This Month",
  "Last Month",
  "Custom",
];

const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: {
      maxHeight: "min(60vh, 420px)",
      maxWidth: "min(92vw, 420px)",
    },
  },
  MenuListProps: {
    dense: true,
  },
};

const getDevicePlateLabel = (device = {}) =>
  device?.vehicleNumber ||
  device?.plateNumber ||
  device?.plateNo ||
  device?.registrationNo ||
  device?.registrationNumber ||
  "";

const pad2 = (n) => String(n).padStart(2, "0");
const toYMD = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const safeFileName = (s = "report") => String(s).replace(/[^\w.-]+/g, "_");
const isObjectLike = (v) => v && typeof v === "object" && !Array.isArray(v);

const toNumberOrNull = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const pickBestCoordinates = (item = {}) => {
  const candidates = [
    [item.lat, item.lng ?? item.lon ?? item.long],
    [item.latitude, item.longitude],
    [item.startLat, item.startLng ?? item.startLon],
    [item.endLat, item.endLng ?? item.endLon],
    [item?.location?.lat, item?.location?.lng ?? item?.location?.lon ?? item?.location?.long],
    [item?.gps?.lat, item?.gps?.lng ?? item?.gps?.lon ?? item?.gps?.long],
  ];

  for (const [latRaw, lonRaw] of candidates) {
    const lat = toNumberOrNull(latRaw);
    const lon = toNumberOrNull(lonRaw);
    if (lat !== null && lon !== null && lat !== 0 && lon !== 0) {
      return { lat, lon };
    }
  }
  return null;
};

const normalizeRowsFromResponse = (result) => {
  if (Array.isArray(result)) return result;
  if (!isObjectLike(result)) return [];

  const looksLikeEnvelope =
    Object.prototype.hasOwnProperty.call(result, "success") ||
    Object.prototype.hasOwnProperty.call(result, "totalCount") ||
    Object.prototype.hasOwnProperty.call(result, "page") ||
    Object.prototype.hasOwnProperty.call(result, "pageSize");

  if (looksLikeEnvelope && isObjectLike(result?.data)) {
    const nested = normalizeRowsFromResponse(result.data);
    if (Array.isArray(nested)) return nested;
  }

  // Common API envelope variants
  if (Array.isArray(result?.rows)) return result.rows;
  if (Array.isArray(result?.data)) return result.data;
  if (isObjectLike(result?.data)) {
    const nested = normalizeRowsFromResponse(result.data);
    if (Array.isArray(nested) && nested.length > 0) return nested;
  }
  if (isObjectLike(result?.result)) {
    const nested = normalizeRowsFromResponse(result.result);
    if (Array.isArray(nested) && nested.length > 0) return nested;
  }
  if (isObjectLike(result?.payload)) {
    const nested = normalizeRowsFromResponse(result.payload);
    if (Array.isArray(nested) && nested.length > 0) return nested;
  }

  const arrayKeys = [
    "rows",
    "data",
    "records",
    "items",
    "list",
    "summaries",
    "trips",
    "events",
    "logs",
    "history",
    "results",
    "entries",
  ];

  for (const key of arrayKeys) {
    if (Array.isArray(result[key])) return result[key];
  }

  const numericKeys = Object.keys(result).filter((k) => /^\d+$/.test(k));
  if (numericKeys.length > 0) {
    return numericKeys
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => result[k]);
  }

  // Avoid treating envelope objects as a table row
  const envelopeKeys = ["success", "message", "error", "status", "totalCount", "page", "pageSize"];
  const hasOnlyEnvelopeKeys = Object.keys(result).every((k) => envelopeKeys.includes(k));
  if (hasOnlyEnvelopeKeys) return [];

  return [result];
};

const withDeviceNameFirst = (rows = [], selectedDevice = null) =>
  rows.map((row) => {
    if (!row || typeof row !== "object") return row;
    if (row.deviceName) return row;
    return {
      ...row,
      deviceName: selectedDevice?.name || selectedDevice?.vehicleName || selectedDevice?.imei || "Unknown Device",
    };
  });

const formatCellValue = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    if (!value.length) return "[]";
    const primitive = value.every((x) => ["string", "number", "boolean"].includes(typeof x));
    if (primitive) return value.join(", ");
    return `Array(${value.length})`;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (isObjectLike(value)) {
    if (typeof value.lat !== "undefined" && typeof value.lng !== "undefined") {
      return `${value.lat}, ${value.lng}`;
    }
    if (typeof value.lat !== "undefined" && typeof value.lon !== "undefined") {
      return `${value.lat}, ${value.lon}`;
    }
    const compact = JSON.stringify(value);
    if (!compact) return "N/A";
    return compact.length > 120 ? `${compact.slice(0, 117)}...` : compact;
  }
  return String(value);
};

const looksLikeLatLngText = (value) => {
  if (typeof value !== "string") return false;
  const v = value.trim();
  return /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(v);
};

const toNumericMetric = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const match = value.replace(/,/g, "").match(/-?\d+(\.\d+)?/);
    if (match) return Number(match[0]);
  }
  return 0;
};

const durationToHours = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 24 ? value / 3600 : value;
  }

  if (typeof value !== "string") return 0;

  const normalized = value.trim().toLowerCase();
  if (!normalized) return 0;

  const clockMatch = normalized.match(/^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/);
  if (clockMatch) {
    const hours = Number(clockMatch[1] || 0);
    const minutes = Number(clockMatch[2] || 0);
    const seconds = Number(clockMatch[3] || 0);
    return hours + minutes / 60 + seconds / 3600;
  }

  const hourMatch = normalized.match(/(-?\d+(?:\.\d+)?)\s*h/);
  const minuteMatch = normalized.match(/(-?\d+(?:\.\d+)?)\s*m/);
  const secondMatch = normalized.match(/(-?\d+(?:\.\d+)?)\s*s/);
  if (hourMatch || minuteMatch || secondMatch) {
    const hours = Number(hourMatch?.[1] || 0);
    const minutes = Number(minuteMatch?.[1] || 0);
    const seconds = Number(secondMatch?.[1] || 0);
    return hours + minutes / 60 + seconds / 3600;
  }

  return toNumericMetric(normalized);
};

const sumMetric = (rows = [], keys = []) =>
  rows.reduce((total, row) => {
    const key = keys.find((candidate) => row?.[candidate] !== undefined && row?.[candidate] !== null && row?.[candidate] !== "");
    if (!key) return total;
    return total + toNumericMetric(row[key]);
  }, 0);

const sumDurationMetric = (rows = [], keys = []) =>
  rows.reduce((total, row) => {
    const key = keys.find((candidate) => row?.[candidate] !== undefined && row?.[candidate] !== null && row?.[candidate] !== "");
    if (!key) return total;
    return total + durationToHours(row[key]);
  }, 0);

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  // Delay revoke so the browser has time to start the download
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 200);
}

function getAbsoluteAssetUrl(assetPath) {
  if (!assetPath || typeof window === "undefined") return "";
  try {
    return new URL(assetPath, window.location.origin).toString();
  } catch (_) {
    return "";
  }
}

export default function ReportsPortalMUI({
  isOpen,
  onClose,
  allDevices: propDevices = [],
  rawData,
  onReplayTrip,
  asPage = false,
  initialReportType = "",
  initialSelectedDeviceId = "",
}) {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  const runtimeBrand = useWhiteLabelRuntime();
  const portalActive = asPage || !!isOpen;
  const ALL_VEHICLES_GROUP = "__all__";
  const ALL_VEHICLES_OPTION = "__all_vehicles__";
  const [selectedImei, setSelectedImei] = useState("");
  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);
  const [reportType, setReportType] = useState("Device Logs Report");

  const [timeRange, setTimeRange] = useState("Today");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const [speedLimit, setSpeedLimit] = useState(80);
  const [minTimeGap, setMinTimeGap] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [activePageTab, setActivePageTab] = useState("overview");
  const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState(null);

  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const isDataPushMode = reportType === "Data Push";
  const isChartMode = false;
  const isVoltageReport = reportType === "Voltage Report";
  const isManagedTripReport = reportType === "Trip Report";
  const isExpenseReport = reportType === "Expense Report";

  const [previewRows, setPreviewRows] = useState([]);
  const [rawPreviewJson, setRawPreviewJson] = useState(null);
  const [misRows, setMisRows] = useState([]);
  const [misTotal, setMisTotal] = useState(0);
  const [misMeta, setMisMeta] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const reportsDisabled = currentUser?.permissions?.other?.disableReports === true;
  const canAccessDataPushReports = currentUser?.permissions?.dataPush?.allow !== false;
  const downloadMenuOpen = Boolean(downloadMenuAnchorEl);
  const { data: groups = [] } = useGetGroupsQuery(undefined, {
    skip: !portalActive,
  });

  const { data: reduxDevices = [], isLoading: devicesLoading } = useGetAllLiteDevicesQuery();

  const [triggerPreview, previewState] = useLazyGetReportPreviewQuery();
  const [triggerDownload, downloadState] = useLazyDownloadReportQuery();
  const [triggerMisLogs, misLogsState] = useLazyGetMoveInSyncPushLogsQuery();
  const [triggerMisCsv, misCsvState] = useLazyDownloadMoveInSyncPushLogsCsvQuery();
  // Local downloading flag — RTK Query cache can return instantly for same params
  // making isFetching never flip true, so we track it manually.
  const [isDownloading, setIsDownloading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const allDevices = useMemo(
    () => (propDevices?.length > 0 ? propDevices : reduxDevices),
    [propDevices, reduxDevices]
  );

  const availableReportTypes = useMemo(
    () => {
      const baseTypes = [...CANONICAL_REPORT_TYPES];
      if (reportType && !baseTypes.includes(reportType) && BACKEND_TYPE_MAP[reportType]) {
        baseTypes.push(reportType);
      }
      if (canAccessDataPushReports) {
        baseTypes.push("Data Push");
      }
      return baseTypes;
    },
    [canAccessDataPushReports, reportType]
  );

  const devicesList = useMemo(() => {
    if (!Array.isArray(allDevices)) return [];
    return allDevices.flat(Infinity).filter((d) => d && (d.imei || d._id));
  }, [allDevices]);
  const groupDeviceMembership = useMemo(() => {
    const membership = new Map();
    (Array.isArray(groups) ? groups : []).forEach((group) => {
      const groupId = String(group?._id || "").trim();
      if (!groupId) return;
      (Array.isArray(group?.devices) ? group.devices : []).forEach((deviceId) => {
        const normalizedDeviceId = String(deviceId?._id || deviceId || "").trim();
        if (!normalizedDeviceId) return;
        const current = membership.get(normalizedDeviceId) || new Set();
        current.add(groupId);
        membership.set(normalizedDeviceId, current);
      });
    });
    return membership;
  }, [groups]);

  const resetView = useCallback(() => {
    setError(null);
    setSearchTerm("");
    setPreviewRows([]);
    setRawPreviewJson(null);
    setMisRows([]);
    setMisTotal(0);
    setMisMeta(null);
    setPage(0);
  }, []);

  useEffect(() => {
    if (rawData?.[0]) {
      const device = Array.isArray(rawData[0]) ? rawData[0][0] : rawData[0];
      if (device) setSelectedImei(String(device.imei || device._id));
    }
  }, [rawData]);

  useEffect(() => {
    const requestedDeviceId = String(initialSelectedDeviceId || "").trim();
    if (!requestedDeviceId || rawData?.[0] || !devicesList.length) return;

    const matchedDevice = devicesList.find((device) => {
      const deviceId = String(device?._id || device?.id || "").trim();
      const deviceImei = String(device?.imei || "").trim();
      return deviceId === requestedDeviceId || deviceImei === requestedDeviceId;
    });

    if (matchedDevice) {
      setSelectedImei(String(matchedDevice.imei || matchedDevice._id || matchedDevice.id || ""));
    }
  }, [devicesList, initialSelectedDeviceId, rawData]);

  useEffect(() => {
    if (!initialReportType) return;
    setReportType(initialReportType);
    resetView();
  }, [initialReportType, resetView]);

  useEffect(() => {
    if (canAccessDataPushReports) return;
    if (reportType === "Data Push") {
      setReportType("Device Logs Report");
    }
  }, [canAccessDataPushReports, reportType]);

  const selectedDevice = useMemo(() => {
    if (!selectedImei) return null;
    return devicesList.find((d) => String(d.imei || d._id) === String(selectedImei)) || null;
  }, [selectedImei, devicesList]);

  const isGroupSummaryReport = reportType === "Summary Report" || reportType === "Group Summary";

  const summaryAvailableDevices = useMemo(() => {
    if (!isGroupSummaryReport) return [];
    if (!selectedGroupId || selectedGroupId === ALL_VEHICLES_GROUP) return devicesList;
    return devicesList.filter((device) => {
      const deviceId = String(device?._id || device?.id || "").trim();
      const rawGroupId = String(device?.deviceGroup?._id || device?.deviceGroup || device?.group?._id || device?.group || "").trim();
      const membershipGroupIds = groupDeviceMembership.get(deviceId) || new Set();
      return rawGroupId === String(selectedGroupId) || membershipGroupIds.has(String(selectedGroupId));
    });
  }, [isGroupSummaryReport, selectedGroupId, devicesList, groupDeviceMembership]);
  const summaryVehicleOptions = useMemo(
    () => [
      {
        _id: ALL_VEHICLES_OPTION,
        name: `All Vehicles (${summaryAvailableDevices.length})`,
        label: `All Vehicles (${summaryAvailableDevices.length})`,
        imei: "ALL",
      },
      ...summaryAvailableDevices,
    ],
    [summaryAvailableDevices]
  );

  useEffect(() => {
    if (!isGroupSummaryReport) return;
    const allowed = new Set(summaryAvailableDevices.map((d) => String(d._id || d.imei)));
    setSelectedVehicleIds((prev) => {
      const next = prev.filter((id) => id === ALL_VEHICLES_OPTION || allowed.has(String(id)));
      if (next.includes(ALL_VEHICLES_OPTION) && next.length > 1) {
        return [ALL_VEHICLES_OPTION];
      }
      return next;
    });
  }, [isGroupSummaryReport, summaryAvailableDevices]);

  const resolvedSummaryVehicleIds = useMemo(() => {
    if (!isGroupSummaryReport) return [];
    if (selectedVehicleIds.includes(ALL_VEHICLES_OPTION)) {
      return summaryAvailableDevices.map((device) => String(device._id || device.imei));
    }
    return selectedVehicleIds;
  }, [isGroupSummaryReport, selectedVehicleIds, summaryAvailableDevices]);


  const getDateRangeYMD = useCallback(() => {
    const now = new Date();
    let start, end;

    if (timeRange === "Custom") {
      if (!customStartDate || !customEndDate) return null;
      return { startDate: customStartDate, endDate: customEndDate };
    }

    switch (timeRange) {
      case "Today":
        start = new Date(now);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "Yesterday":
        start = new Date(now);
        start.setDate(start.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(start);
        end.setHours(23, 59, 59, 999);
        break;
      case "Last 7 Days":
        start = new Date(now);
        start.setDate(start.getDate() - 6);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "Last 30 Days":
        start = new Date(now);
        start.setDate(start.getDate() - 29);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "This Month":
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
        break;
      case "Last Month":
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);
        break;
      default:
        start = new Date(now);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
    }

    return { startDate: toYMD(start), endDate: toYMD(end) };
  }, [timeRange, customStartDate, customEndDate]);

  const logReportRequest = useCallback((mode, payload, extra = {}) => {
    const debugPacket = {
      mode,
      reportType,
      backendType: payload?.type || null,
      timeRange,
      selectedImei: selectedImei || null,
      selectedDeviceId: selectedDevice?._id || null,
      selectedDeviceName: selectedDevice?.name || null,
      selectedDeviceImei: selectedDevice?.imei || null,
      selectedGroupId: selectedGroupId || null,
      customStartDate: customStartDate || null,
      customEndDate: customEndDate || null,
      payload,
      ...extra,
    };

    console.log("REPORT REQUEST:", debugPacket);
  }, [
    reportType,
    timeRange,
    selectedImei,
    selectedDevice,
    selectedGroupId,
    customStartDate,
    customEndDate,
  ]);



  const processReportData = async (data) => {
    if (!Array.isArray(data)) return [];
    const isFuelReport = resolveBackendReportType(reportType) === "fuel";
    
    const processed = [];
    let previousItem = null;
    
    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      if (!item || typeof item !== 'object') continue;
      
      const processedItem = { ...item };
      
      // Fuel rows are daily aggregates, not location events. Do not attach a
      // misleading "No Location" address to them (or include it in exports).
      const coord = isFuelReport ? null : pickBestCoordinates(item);
      if (coord) {
        processedItem.latitude = coord.lat;
        processedItem.longitude = coord.lon;
        const hasBackendAddress =
          typeof processedItem.address === "string" &&
          processedItem.address.trim() &&
          processedItem.address !== "No Location" &&
          processedItem.address !== "NA";

        if (!hasBackendAddress) {
          try {
            const address = await getAddressFromCoordinates(coord.lat, coord.lon);
            processedItem.address = address;
          } catch (error) {
            processedItem.address = `${coord.lat}, ${coord.lon}`;
          }
        }
      } else if (!isFuelReport) {
        const hasBackendAddress =
          typeof processedItem.address === "string" &&
          processedItem.address.trim() &&
          processedItem.address !== "NA";

        if (!hasBackendAddress) {
          processedItem.address = "No Location";
        }
      }

      // Movement/Trip style start-end addresses
      const startLat = toNumberOrNull(item.startLatitude ?? item.startLat);
      const startLng = toNumberOrNull(item.startLongitude ?? item.startLng ?? item.startLon);
      if (startLat !== null && startLng !== null && startLat !== 0 && startLng !== 0) {
        if (
          !processedItem.startAddress ||
          processedItem.startAddress === "NA" ||
          processedItem.startAddress === "No Location" ||
          looksLikeLatLngText(processedItem.startAddress)
        ) {
          try {
            processedItem.startAddress = await getAddressFromCoordinates(startLat, startLng);
          } catch {
            processedItem.startAddress = `${startLat.toFixed(6)}, ${startLng.toFixed(6)}`;
          }
        }
      }

      const endLat = toNumberOrNull(item.endLatitude ?? item.endLat);
      const endLng = toNumberOrNull(item.endLongitude ?? item.endLng ?? item.endLon);
      if (endLat !== null && endLng !== null && endLat !== 0 && endLng !== 0) {
        if (
          !processedItem.endAddress ||
          processedItem.endAddress === "NA" ||
          processedItem.endAddress === "No Location" ||
          looksLikeLatLngText(processedItem.endAddress)
        ) {
          try {
            processedItem.endAddress = await getAddressFromCoordinates(endLat, endLng);
          } catch {
            processedItem.endAddress = `${endLat.toFixed(6)}, ${endLng.toFixed(6)}`;
          }
        }
      }
      
      // Format time fields
      if (item.timestamp) {
        processedItem.formattedTime = formatTime(item.timestamp);
      }
      if (item.startTime) {
        processedItem.formattedStartTime = formatTime(item.startTime);
      }
      if (item.endTime) {
        processedItem.formattedEndTime = formatTime(item.endTime);
      }
      
      // Calculate duration only when backend hasn't already provided it
      if (!item.duration && (item.startTimestamp || item.startTime) && (item.endTimestamp || item.endTime)) {
        processedItem.duration = calculateDuration(
          item.startTimestamp || item.startTime,
          item.endTimestamp || item.endTime
        );
      }
      
      // Calculate time gap from previous record
      if (previousItem && item.timestamp) {
        const gap = calculateTimeGap(item.timestamp, previousItem.timestamp);
        if (gap !== null) {
          processedItem.timeGap = gap;
          processedItem.timeGapDisplay = gap < 1 ? 
            `${Math.round(gap * 60)}s` : 
            `${Math.round(gap)}m`;
        }
      }
      
      processed.push(processedItem);
      previousItem = item;
    }
    
    return processed;
  };

  const fetchMisLogs = useCallback(
    async ({ muiPage0 = page, muiLimit = rowsPerPage } = {}) => {
      if (!selectedImei) return;

      const range = getDateRangeYMD();
      if (!range) {
        setError("Please select valid dates");
        return;
      }

      setError(null);

      try {
        const payload = {
          deviceId: selectedImei,
          ...range,
          page: muiPage0 + 1,
          limit: muiLimit,
          search: searchTerm?.trim() || undefined,
        };

        const res = await triggerMisLogs(payload).unwrap();
        const list = Array.isArray(res?.rows) ? res.rows : [];
        
        setMisRows(list);
        setMisTotal(Number(res?.total || 0));
        setMisMeta(res);

        if (!list.length) {
          setError("No logs found");
        }
      } catch (e) {
        setError(e?.data?.message || e?.data?.msg || e?.message || "Failed to load logs");
      }
    },
    [page, rowsPerPage, selectedImei, getDateRangeYMD, triggerMisLogs, searchTerm]
  );

// ✅ FIXED handleGenerate (no processedRows undef, chart + normal both supported)
const handleGenerate = async () => {
  if (reportsDisabled) {
    setError("Reports access is disabled for this account.");
    return;
  }

  // ✅ Vehicle / Group validations
if (isGroupSummaryReport) {
  const count = summaryAvailableDevices.length;
  const selectedCount = resolvedSummaryVehicleIds.length;
  if (count === 0) return setError("No vehicles available for summary report.");
  if (selectedCount === 0) return setError("Please select at least one vehicle");
}else {
    if (!selectedImei && !isVoltageReport && !isManagedTripReport && !isExpenseReport) {
      setError("Please select a vehicle");
      return;
    }
  }

  const range = getDateRangeYMD();
  if (!range) {
    setError("Please select valid dates");
    return;
  }

  setError(null);
  setPage(0);
  setIsGenerating(true);

  // ✅ Data Push mode
  if (isDataPushMode) {
    try {
      logReportRequest("preview:data-push", {
        deviceId: selectedImei || null,
        ...range,
        search: searchTerm?.trim() || undefined,
        page: 1,
        pageSize: rowsPerPage,
      });
      await fetchMisLogs({ muiPage0: 0, muiLimit: rowsPerPage });
    } finally {
      setIsGenerating(false);
    }
    return;
  }

  try {
    setPreviewRows([]);
    setRawPreviewJson(null);

    const type = resolveBackendReportType(reportType);

    const params = { type, ...range };

    if (selectedImei) {
      params.deviceId = selectedImei;
    } else if (isVoltageReport || isManagedTripReport || isExpenseReport) {
      params.deviceIds = devicesList
        .map((d) => String(d._id || d.imei))
        .join(",");
    }

    // ✅ Group Summary needs groupId
    if (isGroupSummaryReport) {
      params.deviceIds = resolvedSummaryVehicleIds.join(",");
    }

    // ✅ Overspeed needs speedLimit
    if (reportType === "Overspeed Report") {
      params.speedLimit = speedLimit;
    }

    logReportRequest("preview", params, {
      resolvedRange: range,
      isGroupSummaryReport,
      resolvedSummaryVehicleIds,
    });

    const result = await triggerPreview(params).unwrap();
    setRawPreviewJson(result);

    if (result?.success === false) {
      setError(result?.message || "Failed to generate report");
      return;
    }

    let rows = [];
    let processedRows = []; // ✅ IMPORTANT: define in outer scope

    // ✅ Chart special handling
    if (type === "chart" && result?.chartData) {
      const cd = result.chartData;

      const labels = Array.isArray(cd.labels) ? cd.labels : [];
      const speed = Array.isArray(cd.speed) ? cd.speed : [];
      const distance = Array.isArray(cd.distance) ? cd.distance : [];
      const fuel = Array.isArray(cd.fuel) ? cd.fuel : [];
      const temperature = Array.isArray(cd.temperature) ? cd.temperature : [];

      rows = labels.map((label, i) => ({
        time: label,
        speed: Number(speed[i] ?? 0),
        distance: Number(distance[i] ?? 0),
        fuel: Number(fuel[i] ?? 0),
        temperature: Number(temperature[i] ?? 0),
      }));

      setPreviewRows(rows);

      // ✅ chart empty check
      if (!rows.length) {
        setError("No data found");
      }

      return; // ✅ stop here (chart has no table processing)
    }

    // ✅ Normal reports
    rows = normalizeRowsFromResponse(result);

    processedRows = await processReportData(rows);
    processedRows = withDeviceNameFirst(processedRows, selectedDevice);
    setPreviewRows(processedRows);

    if (!processedRows.length) {
      setError("No data found");
    }
  } catch (e) {
    setError(e?.data?.message || e?.data?.msg || e?.message || "Failed to generate report");
  } finally {
    setIsGenerating(false);
  }
};


  const handleDownload = async (format) => {
    if (isDownloading) return; // prevent concurrent downloads
    if (reportsDisabled) {
      return setError("Reports access is disabled for this account.");
    }

    if (!selectedImei && !isGroupSummaryReport) return setError("Please select a vehicle");

    const range = getDateRangeYMD();
    if (!range) return setError("Please select valid dates");

    setIsDownloading(true);
    setError(null);

    if (isDataPushMode) {
      try {
        if (format === "csv") {
          logReportRequest("download:data-push", {
            deviceId: selectedImei || null,
            ...range,
            search: searchTerm?.trim() || undefined,
            format,
          });
          const blob = await triggerMisCsv({
            deviceId: selectedImei,
            ...range,
            search: searchTerm?.trim() || undefined,
          }).unwrap();

          const deviceName = selectedDevice?.name || selectedDevice?.imei || "Device";
          const stamp = new Date().toISOString().slice(0, 10);
          downloadBlob(blob, `${safeFileName(deviceName)}_push_logs_${stamp}.csv`);
          return;
        }

        const deviceName = selectedDevice?.name || selectedDevice?.imei || "Device";
        const stamp = new Date().toISOString().slice(0, 10);
        const blob = new Blob([JSON.stringify({ total: misTotal, rows: misRows, meta: misMeta }, null, 2)], {
          type: "application/json",
        });
        downloadBlob(blob, `${safeFileName(deviceName)}_push_logs_${stamp}.json`);
      } catch (e) {
        setError(e?.data?.message || e?.data?.msg || e?.message || "Download failed");
      } finally {
        setIsDownloading(false);
      }
      return;
    }

    try {
      const type = resolveBackendReportType(reportType);
      const params = {
        type,
        format,
        ...range,
        brandName: runtimeBrand?.brandTitle || undefined,
        // A white-label logo takes priority. Otherwise send this CRM's actual
        // bundled logo so the server never falls back to its legacy icon.
        brandLogoUrl:
          runtimeBrand?.logoUrl || getAbsoluteAssetUrl(defaultWebCrmLogo) || undefined,
      };
      if (selectedImei) {
        params.deviceId = selectedImei;
      } else if (isVoltageReport || isManagedTripReport || isExpenseReport) {
        params.deviceIds = devicesList
          .map((d) => String(d._id || d.imei))
          .join(",");
      }
      if (isGroupSummaryReport) {
        params.deviceIds = resolvedSummaryVehicleIds.join(",");
      }
      if (reportType === "Overspeed Report") params.speedLimit = speedLimit;

      logReportRequest("download", params, {
        resolvedRange: range,
        format,
        isGroupSummaryReport,
        resolvedSummaryVehicleIds,
      });

      // preferCacheValue: false forces a fresh request every time,
      // preventing stale blobs from being returned from the RTK Query cache.
      const blob = await triggerDownload(params, { preferCacheValue: false }).unwrap();
      const ext = format === "excel" ? "xlsx" : format;
      const deviceName = selectedDevice?.name || selectedDevice?.imei || "Device";
      const stamp = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `${safeFileName(deviceName)}_${safeFileName(type)}_${stamp}.${ext}`);
    } catch (e) {
      setError(e?.data?.message || e?.data?.msg || e?.message || "Download failed");
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    if (!portalActive) return;
    if (!isDataPushMode) return;
    if (!misMeta) return;
    fetchMisLogs({ muiPage0: page, muiLimit: rowsPerPage });
  }, [page, rowsPerPage, portalActive, isDataPushMode, misMeta, fetchMisLogs]);

  const filteredRows = useMemo(() => {
    let filtered = isDataPushMode ? misRows : previewRows;
    
    if (filtered.length === 0) return [];
    
    // Apply search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(row => {
        return Object.values(row || {}).some(value => 
          String(value).toLowerCase().includes(term)
        );
      });
    }
    
    // Apply time gap filter
    if (minTimeGap > 0 && !isDataPushMode) {
      filtered = filtered.filter((row, index, array) => {
        if (index === 0) return true;
        const gap = row.timeGap;
        return gap === undefined || gap >= minTimeGap;
      });
    }
    
    return filtered;
  }, [isDataPushMode, misRows, previewRows, searchTerm, minTimeGap]);

  // ✅ Dynamic columns from data
  const tableColumns = useMemo(() => {
    if (filteredRows.length === 0) return [];
    
    const sampleRows = filteredRows.slice(0, 20);
    const keySet = new Set();
    sampleRows.forEach((row) => {
      if (!row || typeof row !== "object") return;
      Object.keys(row).forEach((k) => keySet.add(k));
    });
    const allKeys = Array.from(keySet);
    if (!allKeys.length) return [];
    
    // Sort keys to show important ones first
const importantKeys = [
  "deviceName",
  "event",
  "startTime",
  "startAddress",
  "endTime",
  "endAddress",
  "duration",
  "distance",
  "distanceKm",
  "time",
  "formattedTime",
  "address",
  "timeGapDisplay",
  "speed",
  "fuel",
  "temperature",
];

    
    const hiddenKeys = ["_id", "__v", "deviceId", "replayData"];
    const conditionalHiddenKeys =
      reportType === "Daily Summary Report" || reportType === "Daily Summary"
        ? ["address", "replayData"]
        : reportType === "Engine Report"
          ? ["address", "replayData"]
        : reportType === "Trip History Report"
          ? ["address", "startAddress", "endAddress", "replayData"]
        : reportType === "Movement Report"
          ? ["address", "replayData", "formattedStartTime", "formattedEndTime"]
        : reportType === "Overspeed Report"
          ? ["address", "replayData", "formattedStartTime", "formattedEndTime"]
        : reportType === "Idle Report"
          ? ["address"]
        : reportType === "Distance Report"
          ? ["address"]
        : reportType === "Voltage Report"
          ? ["address"]
        : reportType === "AC Report" || reportType === "ACC / Ignition Summary"
          ? ["address"]
        : reportType === "Stoppage Report"
          ? [
              "formattedStartTime",
              "formattedEndTime",
              "lat",
              "lng",
              "latitude",
              "longitude",
            ]
        : reportType === "Chart"
          ? ["address", "replayData"]
        : reportType === "Summary Report"
          ? ["address", "replayData"]
        : reportType === "Group Summary"
          ? ["address", "replayData"]
        : reportType === "Geofence Trip"
          ? ["address", "replayData", "formattedStartTime", "formattedEndTime"]
        : reportType === "Expense Report"
          ? ["address"]
        : reportType === "Fuel Report" || reportType === "Fuel Reports"
          ? ["address"]
          : [];
    const sortedKeys = [
      ...importantKeys.filter(key => allKeys.includes(key)),
      ...allKeys.filter(
        (key) =>
          !importantKeys.includes(key) &&
          !hiddenKeys.includes(key) &&
          !conditionalHiddenKeys.includes(key) &&
          !key.startsWith("_")
      )
    ];

    const visibleKeys = sortedKeys.filter((key) => !conditionalHiddenKeys.includes(key));
    
    return visibleKeys.map(key => ({
      key,
      label: key.replace(/([A-Z])/g, ' $1')
                .replace(/^./, str => str.toUpperCase())
                .replace('Formatted ', '')
                .replace('TimeGap', 'Time Gap')
    }));
  }, [filteredRows, reportType]);

  const handleDownloadPreviewCsv = useCallback(() => {
    if (!filteredRows.length) return;
    const columns = isDataPushMode
      ? [
          { key: "time", label: "Time" },
          { key: "imei", label: "IMEI" },
          { key: "integrationName", label: "Integration" },
          { key: "endpoint", label: "Endpoint" },
          { key: "success", label: "Status" },
          { key: "responseCode", label: "Response Code" },
          { key: "error", label: "Error" },
        ]
      : tableColumns;

    const lines = [];
    lines.push(columns.map((c) => `"${String(c.label).replace(/"/g, '""')}"`).join(","));
    filteredRows.forEach((row) => {
      const line = columns
        .map((c) => {
          const raw = row?.[c.key];
          const value =
            raw === null || raw === undefined
              ? ""
              : typeof raw === "object"
                ? JSON.stringify(raw)
                : String(raw);
          return `"${value.replace(/"/g, '""')}"`;
        })
        .join(",");
      lines.push(line);
    });

    const csv = lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const stamp = new Date().toISOString().slice(0, 10);
    const fileBase = isDataPushMode ? "data_push_preview" : "report_preview";
    downloadBlob(blob, `${fileBase}_${stamp}.csv`);
  }, [filteredRows, tableColumns, isDataPushMode]);

  const loading = devicesLoading || previewState.isLoading || misLogsState.isLoading || isGenerating;
  const downloadBusy = isDownloading || downloadState.isFetching || misCsvState.isFetching;
  const kpiSummary = useMemo(() => {
    const rows = filteredRows || [];
    const uniqueVehicles = new Set(
      rows
        .map((row) => row?.deviceName || row?.vehicleNumber || row?.imei || row?.deviceId)
        .filter(Boolean)
    ).size;
    const totalDistance = sumMetric(rows, [
      "distance",
      "distanceKm",
      "totalDistance",
      "distanceTravelled",
      "total_distance",
      "distance_covered",
      "travelDistance",
      "tripDistance",
      "km",
    ]);
    // A stoppage duration is parked/off time, not engine-running time.
    const totalEngineHours = reportType === "Stoppage Report" ? 0 : sumDurationMetric(rows, [
      "engineHours",
      "workingHours",
      "runTime",
      "hours",
      "duration",
      "workingDuration",
      "engineDuration",
      "idleDuration",
      "totalDuration",
      "runningTime",
      "totalWorkingHours",
    ]);

    return [
      {
        key: "vehicles",
        label: "Total Vehicles",
        value: uniqueVehicles || (selectedImei || isGroupSummaryReport ? 1 : 0),
        tone: "#2563eb",
        caption: "In selected range",
        icon: <DirectionsCarFilledOutlinedIcon />,
        className: "is-blue",
      },
      {
        key: "records",
        label: "Total Records",
        value: rows.length || 0,
        tone: "#16a34a",
        caption: "Generated records",
        icon: <DescriptionRoundedIcon />,
        className: "is-green",
      },
      {
        key: "distance",
        label: "Total Distance",
        value: `${totalDistance ? totalDistance.toFixed(2) : "0.00"} km`,
        tone: "#f59e0b",
        caption: "Travelled",
        icon: <RouteOutlinedIcon />,
        className: "is-amber",
      },
      {
        key: "engine",
        label: "Total Engine Hours",
        value: `${totalEngineHours ? totalEngineHours.toFixed(1) : "0.0"} h`,
        tone: "#8b5cf6",
        caption: "In selected range",
        icon: <TimerOutlinedIcon />,
        className: "is-violet",
      },
    ];
  }, [filteredRows, selectedImei, isGroupSummaryReport, reportType]);

  const trendSeries = useMemo(() => {
    if (!Array.isArray(filteredRows) || filteredRows.length === 0) return [];
    const numericKey = ["engineHours", "workingHours", "distance", "distanceKm", "speed", "fuel", "temperature"].find((key) =>
      filteredRows.some((row) => toNumericMetric(row?.[key]) > 0)
    );
    return filteredRows.slice(0, 24).map((row, index) => ({
      label:
        row?.formattedTime ||
        row?.formattedStartTime ||
        row?.time ||
        row?.startTime ||
        row?.date ||
        `#${index + 1}`,
      value: numericKey ? toNumericMetric(row?.[numericKey]) : 0,
    }));
  }, [filteredRows]);

  const statusBreakdown = useMemo(() => {
    const sourceRows = Array.isArray(filteredRows) ? filteredRows : [];
    const counts = new Map();
    sourceRows.forEach((row) => {
      const rawStatus = row?.status || row?.event || row?.ignition || row?.state || "Available";
      const label = String(rawStatus).trim() || "Available";
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    const baseColors = ["#22c55e", "#ef4444", "#f59e0b", "#0ea5e9", "#8b5cf6", "#94a3b8"];
    return Array.from(counts.entries())
      .slice(0, 6)
      .map(([name, value], index) => ({
        name,
        value,
        color: baseColors[index % baseColors.length],
      }));
  }, [filteredRows]);

  const pageTabs = [
    { key: "overview", label: "Overview", icon: <HomeOutlinedIcon fontSize="small" /> },
    { key: "data", label: "Report Data", icon: <TableChartOutlinedIcon fontSize="small" /> },
    { key: "graph", label: "Graph View", icon: <BarChartOutlinedIcon fontSize="small" /> },
  ];

  const reportsPageSearchableSelectSx = {
    "& .MuiOutlinedInput-root": {
      minHeight: 48,
      background: "rgba(10, 22, 48, 0.96)",
      color: "#fff",
      borderRadius: 3,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(148, 163, 184, 0.32)",
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(191, 219, 254, 0.5)",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#3b82f6",
    },
    "& .MuiInputLabel-root": {
      color: "rgba(226, 232, 240, 0.88)",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#bfdbfe",
    },
    "& .MuiAutocomplete-input": {
      color: "#fff !important",
      WebkitTextFillColor: "#fff",
    },
    "& .MuiAutocomplete-endAdornment .MuiButtonBase-root": {
      color: "rgba(226, 232, 240, 0.92)",
    },
    "& .MuiSvgIcon-root": {
      color: "rgba(226, 232, 240, 0.92)",
    },
    "& .MuiInputBase-input::placeholder": {
      color: "rgba(226, 232, 240, 0.58)",
      opacity: 1,
    },
  };

  const reportsPageSearchableTextFieldProps = {
    InputLabelProps: {
      sx: { color: "rgba(226, 232, 240, 0.88)" },
    },
  };

  const getVehicleSelectValue = (d) => {
    if (isDataPushMode) return d._id;
    return d.imei || d._id;
  };

  const selectedTimeLabel = timeRange === "Custom"
    ? `${customStartDate || "Start"} - ${customEndDate || "End"}`
    : timeRange;
  const generationStatusLabel = isDataPushMode
    ? `Generating ${reportType}...`
    : `Generating ${reportType} for ${selectedTimeLabel}...`;

  const downloadOptions = isDataPushMode
    ? [
        { key: "csv", label: "CSV" },
        { key: "json", label: "JSON" },
      ]
    : [
        { key: "excel", label: "Excel" },
        { key: "pdf", label: "PDF" },
      ];

  const filterSection = (
    <div className="reports-page-controlbar">
      <div className="reports-page-controlbar__segment reports-page-controlbar__segment--type">
        <div className="reports-page-controlbar__heading">
          <span className="reports-page-controlbar__badge">
            <DescriptionRoundedIcon fontSize="small" />
          </span>
          <div>
            <strong>1. Report Type</strong>
          </div>
        </div>
        <FormControl fullWidth size="small" className="reports-page-dark-field">
          <InputLabel>Report Type</InputLabel>
          <Select
            value={reportType}
            label="Report Type"
            MenuProps={SELECT_MENU_PROPS}
            disabled={reportsDisabled}
            onChange={(e) => {
              setReportType(e.target.value);
              resetView();
            }}
          >
            {availableReportTypes.map((k) => (
              <MenuItem key={k} value={k}>
                {k}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className="reports-page-controlbar__segment reports-page-controlbar__segment--filters">
        <div className="reports-page-controlbar__heading">
          <span className="reports-page-controlbar__badge">
            <FilterListIcon fontSize="small" />
          </span>
          <div>
            <strong>2. Filters</strong>
          </div>
        </div>

        <div className="reports-page-filter-grid">
          {isGroupSummaryReport ? (
            <>
              <FormControl fullWidth size="small" className="reports-page-dark-field">
                <InputLabel>Select Group</InputLabel>
                <Select
                  value={selectedGroupId}
                  label="Select Group"
                  MenuProps={SELECT_MENU_PROPS}
                  onChange={(e) => {
                    setSelectedGroupId(e.target.value);
                    resetView();
                  }}
                >
                  <MenuItem value="">
                    <em>All Groups</em>
                  </MenuItem>
                  <MenuItem value={ALL_VEHICLES_GROUP}>
                    All Vehicles ({devicesList.length})
                  </MenuItem>
                  {groups.map((g) => (
                    <MenuItem key={g._id} value={g._id}>
                      {g.name} ({g.devices?.length || 0} devices)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <SearchableDeviceSelect
                multiple
                label="Vehicles"
                options={summaryVehicleOptions}
                value={
                  selectedVehicleIds.includes(ALL_VEHICLES_OPTION)
                    ? summaryVehicleOptions.filter((device) => String(device?._id) === ALL_VEHICLES_OPTION)
                    : summaryVehicleOptions.filter((device) =>
                        selectedVehicleIds.includes(String(device._id || device.imei))
                      )
                }
                onChange={(nextDevices) => {
                  const normalized = (Array.isArray(nextDevices) ? nextDevices : []).map((device) =>
                    String(device?._id || device?.imei)
                  );
                  const next = normalized.includes(ALL_VEHICLES_OPTION)
                    ? [ALL_VEHICLES_OPTION]
                    : normalized.filter((value) => value !== ALL_VEHICLES_OPTION);
                  setSelectedVehicleIds(next);
                  resetView();
                }}
                getOptionLabel={(device) => {
                  const plate = getDevicePlateLabel(device);
                  return plate
                    ? `${device?.name || "Unnamed"} (${device?.imei || device?._id}) • ${plate}`
                    : `${device?.name || "Unnamed"} (${device?.imei || device?._id})`;
                }}
                helperText=""
                sx={reportsPageSearchableSelectSx}
                textFieldProps={reportsPageSearchableTextFieldProps}
              />
            </>
          ) : (
            <SearchableDeviceSelect
              label="Vehicle"
              options={devicesList}
              value={selectedDevice}
              onChange={(device) => {
                setSelectedImei(device ? getVehicleSelectValue(device) : "");
                resetView();
              }}
              getOptionLabel={(device) => {
                const plate = getDevicePlateLabel(device);
                return plate
                  ? `${device?.name || "Unnamed"} (${device?.imei || device?._id}) • ${plate}`
                  : `${device?.name || "Unnamed"} (${device?.imei || device?._id})`;
              }}
              helperText=""
              sx={reportsPageSearchableSelectSx}
              textFieldProps={{
                ...reportsPageSearchableTextFieldProps,
                placeholder: devicesList.length ? "Search vehicle name, IMEI, or plate number" : "No vehicles available",
              }}
            />
          )}

          <FormControl fullWidth size="small" className="reports-page-dark-field">
            <InputLabel>Time Range</InputLabel>
            <Select
              value={timeRange}
              label="Time Range"
              MenuProps={SELECT_MENU_PROPS}
              onChange={(e) => {
                setTimeRange(e.target.value);
                resetView();
              }}
            >
              {TIME_RANGES.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

       
        </div>

        {timeRange === "Custom" ? (
          <div className="reports-page-custom-dates">
            <TextField
              size="small"
              label="Start Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={customStartDate}
              onChange={(e) => {
                setCustomStartDate(e.target.value);
                resetView();
              }}
              className="reports-page-dark-field"
            />
            <TextField
              size="small"
              label="End Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={customEndDate}
              onChange={(e) => {
                setCustomEndDate(e.target.value);
                resetView();
              }}
              className="reports-page-dark-field"
            />
          </div>
        ) : null}

        {reportType === "Overspeed Report" && !isDataPushMode ? (
          <div className="reports-page-speed-limit">
            <TextField
              size="small"
              label="Speed Limit"
              type="number"
              fullWidth
              value={speedLimit}
              onChange={(e) => setSpeedLimit(Number(e.target.value))}
              className="reports-page-dark-field"
            />
          </div>
        ) : null}
      </div>

      <div className="reports-page-controlbar__segment reports-page-controlbar__segment--actions">
        <div className="reports-page-controlbar__heading">
          <span className="reports-page-controlbar__badge">
            <BoltOutlinedIcon fontSize="small" />
          </span>
          <div>
            <strong>3. Actions</strong>
          </div>
        </div>

        <div className="reports-page-action-group">
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleGenerate}
            disabled={loading || (!isGroupSummaryReport && !selectedImei && !isVoltageReport && !isManagedTripReport && !isExpenseReport)}
            className="reports-page-generate-btn"
          >
            {loading ? "Generating Report..." : "Generate Report"}
          </Button>

          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            endIcon={<ExpandMoreIcon />}
            onClick={(event) => setDownloadMenuAnchorEl(event.currentTarget)}
            disabled={loading || downloadBusy || reportsDisabled}
            className="reports-page-download-btn"
          />

          <Menu
            anchorEl={downloadMenuAnchorEl}
            open={downloadMenuOpen}
            onClose={() => setDownloadMenuAnchorEl(null)}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            PaperProps={{ className: "reports-page-download-menu" }}
          >
            {downloadOptions.map((option) => (
              <MuiMenuItem
                key={option.key}
                onClick={() => {
                  setDownloadMenuAnchorEl(null);
                  handleDownload(option.key);
                }}
              >
                <ListItemIcon>
                  <DownloadIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{option.label}</ListItemText>
              </MuiMenuItem>
            ))}
          </Menu>
        </div>
      </div>
    </div>
  );

  const pageContent = asPage ? (
    <div className="reports-page-shell">
      {reportsDisabled ? <Alert severity="warning">Reports access is disabled for this account.</Alert> : null}
      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}
      {loading ? <Alert severity="info" icon={<CircularProgress size={16} color="inherit" />}>{generationStatusLabel}</Alert> : null}

      <div className="reports-page-stage">
        <div className="reports-page-hero">
          <div>
            <h1>Reports</h1>
          </div>

        </div>

        <div className="reports-page-kpis">
          {kpiSummary.map((card) => (
            <div key={card.key} className={`reports-page-kpi-card ${card.className || ""}`}>
              <div className="reports-page-kpi-card__icon">{card.icon}</div>
              <div className="reports-page-kpi-card__content">
                <span>{card.label}</span>
                <strong>{card.value}</strong>
                <small>{card.caption}</small>
              </div>
            </div>
          ))}
        </div>

        {filterSection}

      

        <div className="reports-page-tabs">
          {pageTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`reports-page-tab ${activePageTab === tab.key ? "is-active" : ""}`}
              onClick={() => setActivePageTab(tab.key)}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {(activePageTab === "overview" || activePageTab === "graph") ? (
        <div className="reports-page-overview-grid">
          <Paper className="reports-page-panel" variant="outlined">
            <div className="reports-page-panel__header">
              <strong>{activePageTab === "graph" ? "Graph View" : "Overview"}</strong>
            </div>
            <div className="reports-page-panel__body">
              {trendSeries.length ? (
                <Box sx={{ height: 340 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={trendSeries}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
                      <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                      <RTooltip />
                      <Line type="monotone" dataKey="value" stroke="#2558e6" strokeWidth={3} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box sx={{ py: 10, textAlign: "center", color: "#64748b" }}>No chart data available for this report.</Box>
              )}
            </div>
          </Paper>

          <Paper className="reports-page-panel" variant="outlined">
            <div className="reports-page-panel__header">
              <strong>Status Summary</strong>
            </div>
            <div className="reports-page-panel__body">
              {statusBreakdown.length ? (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "240px 1fr" }, gap: 1.5, alignItems: "center" }}>
                  <Box sx={{ height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={statusBreakdown} dataKey="value" innerRadius={58} outerRadius={92} paddingAngle={2}>
                          {statusBreakdown.map((entry) => (
                            <Cell key={entry.name} fill={entry.color} />
                          ))}
                        </Pie>
                        <RTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                  <Stack spacing={1.1}>
                    {statusBreakdown.map((entry) => (
                      <Stack key={entry.name} direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box sx={{ width: 10, height: 10, borderRadius: 999, bgcolor: entry.color }} />
                          <Typography sx={{ color: "#334155" }}>{entry.name}</Typography>
                        </Stack>
                        <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>{entry.value}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              ) : (
                <Box sx={{ py: 10, textAlign: "center", color: "#64748b" }}>No status summary available.</Box>
              )}
            </div>
          </Paper>
        </div>
      ) : null}

      {activePageTab === "data" ? (
        <Paper className="reports-page-table-card" variant="outlined">
          <div className="reports-page-table-card__header">
            <div className="reports-page-table-card__title">
              <span className="reports-page-table-card__title-icon">
                <DescriptionRoundedIcon fontSize="small" />
              </span>
              <strong>{isDataPushMode ? "Data Push Logs" : "Report Data"}</strong>
              {filteredRows.length > 0 ? <small>({filteredRows.length} records)</small> : null}
            </div>

            <div className="reports-page-table-card__tools">
              <TextField
                size="small"
                placeholder="Search in results..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="reports-page-search"
              />
              <Button
                variant="outlined"
                onClick={handleDownloadPreviewCsv}
                disabled={!filteredRows.length}
                className="reports-page-csv-btn"
              >
                Download CSV
              </Button>
            </div>
          </div>

          <Divider />

          <div className="reports-page-table-card__body">
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>{generationStatusLabel}</Typography>
              </Box>
            ) : (!selectedImei && !isGroupSummaryReport && !isVoltageReport && !isManagedTripReport && !isExpenseReport) ? (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary">Select a vehicle to generate report</Typography>
              </Box>
            ) : isDataPushMode ? (
              !misMeta ? (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">Click Generate to load Data Push logs</Typography>
                </Box>
              ) : filteredRows.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">No logs found</Typography>
                </Box>
              ) : (
                <>
                  <ScrollableTableContainer sx={{ maxHeight: 560 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 900 }}>Time</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>IMEI</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>Integration</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>Endpoint</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>Response Code</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>Error</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                          <TableRow key={row?._id || idx} hover>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{row?.time ? formatTime(row.time) : "N/A"}</TableCell>
                            <TableCell>{row?.imei || selectedDevice?.imei || "N/A"}</TableCell>
                            <TableCell>{row?.integrationName || "MoveInSync"}</TableCell>
                            <TableCell>
                              {row?.endpoint ? (
                                <Tooltip title={row.endpoint}>
                                  <Typography sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {row.endpoint}
                                  </Typography>
                                </Tooltip>
                              ) : "N/A"}
                            </TableCell>
                            <TableCell>
                              <Chip size="small" color={row?.success ? "success" : "error"} label={row?.success ? "Success" : "Failed"} />
                            </TableCell>
                            <TableCell>{row?.responseCode || "N/A"}</TableCell>
                            <TableCell sx={{ color: row?.error ? "error.main" : "text.secondary" }}>
                              {row?.error || "N/A"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollableTableContainer>
                  <TablePagination
                    component="div"
                    count={filteredRows.length}
                    page={page}
                    ActionsComponent={() => null}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                </>
              )
            ) : (
              !rawPreviewJson ? (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">Click Generate to preview report</Typography>
                </Box>
              ) : filteredRows.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">No data found</Typography>
                </Box>
              ) : (
                <>
                  <ScrollableTableContainer sx={{ maxHeight: 560 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {tableColumns.map((col) => (
                            <TableCell key={col.key} sx={{ fontWeight: 900, whiteSpace: "nowrap" }}>
                              {col.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                          <TableRow key={idx} hover>
                            {tableColumns.map((col) => {
                              const value = row[col.key];
                              if (col.key === "address") {
                                return (
                                  <TableCell key={col.key}>
                                    <Tooltip title={value || "No address"}>
                                      <Box display="flex" alignItems="center" gap={0.5}>
                                        <LocationOnIcon fontSize="small" color="primary" />
                                        <Typography sx={{ maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                          {value || "No Location"}
                                        </Typography>
                                      </Box>
                                    </Tooltip>
                                  </TableCell>
                                );
                              }
                              const isTimeField =
                                col.key.toLowerCase().includes("time") ||
                                col.key.toLowerCase().includes("date") ||
                                col.key === "duration" ||
                                col.key === "timeGapDisplay";
                              if (isTimeField) {
                                return (
                                  <TableCell key={col.key} sx={{ whiteSpace: "nowrap" }}>
                                    <Box display="flex" alignItems="center" gap={0.5}>
                                      <AccessTimeIcon fontSize="small" color="action" />
                                      <Typography>{formatCellValue(value)}</Typography>
                                    </Box>
                                  </TableCell>
                                );
                              }
                              const display =
                                col.key === "temperature" && (value === 0 || value === "0")
                                  ? ""
                                  : formatCellValue(value);
                              return (
                                <TableCell key={col.key} sx={{ whiteSpace: "nowrap" }}>
                                  <Tooltip title={display}>
                                    <Typography sx={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {display}
                                    </Typography>
                                  </Tooltip>
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollableTableContainer>
                  <TablePagination
                    component="div"
                    count={filteredRows.length}
                    page={page}
                    ActionsComponent={() => null}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                </>
              )
            )}
          </div>
        </Paper>
      ) : null}
    </div>
  ) : null;

  if (asPage) {
    return pageContent;
  }

  return (
    <Dialog open={!!isOpen} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
        <Stack spacing={0.25}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            Reports Portal
          </Typography>
        </Stack>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        <Stack spacing={2}>
          {reportsDisabled && (
            <Alert severity="warning">
              Reports access is disabled for this account.
            </Alert>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Filters */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Report Type</InputLabel>
                  <Select
                    value={reportType}
                    label="Report Type"
                    MenuProps={SELECT_MENU_PROPS}
                    disabled={reportsDisabled}
                    onChange={(e) => {
                      setReportType(e.target.value);
                      resetView();
                    }}
                  >
                    {availableReportTypes.map((k) => (
                      <MenuItem key={k} value={k}>
                        {k}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {isGroupSummaryReport ? (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Select Group</InputLabel>
                      <Select
                        value={selectedGroupId}
                        label="Select Group"
                        MenuProps={SELECT_MENU_PROPS}
                        onChange={(e) => {
                          setSelectedGroupId(e.target.value);
                          resetView();
                        }}
                      >
                        <MenuItem value="">
                          <em>All Groups</em>
                        </MenuItem>
                        <MenuItem value={ALL_VEHICLES_GROUP}>
                          All Vehicles ({devicesList.length})
                        </MenuItem>
                        {groups.map((g) => (
                          <MenuItem key={g._id} value={g._id}>
                            {g.name} ({g.devices?.length || 0} devices)
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <SearchableDeviceSelect
                      multiple
                      label="Vehicles"
                      options={summaryVehicleOptions}
                      value={
                        selectedVehicleIds.includes(ALL_VEHICLES_OPTION)
                          ? summaryVehicleOptions.filter((device) => String(device?._id) === ALL_VEHICLES_OPTION)
                          : summaryVehicleOptions.filter((device) =>
                              selectedVehicleIds.includes(String(device._id || device.imei))
                            )
                      }
                      onChange={(nextDevices) => {
                        const normalized = (Array.isArray(nextDevices) ? nextDevices : []).map((device) =>
                          String(device?._id || device?.imei)
                        );
                        const next = normalized.includes(ALL_VEHICLES_OPTION)
                          ? [ALL_VEHICLES_OPTION]
                          : normalized.filter((value) => value !== ALL_VEHICLES_OPTION);
                        setSelectedVehicleIds(next);
                        resetView();
                      }}
                      getOptionLabel={(device) => {
                        const plate = getDevicePlateLabel(device);
                        return plate
                          ? `${device?.name || "Unnamed"} (${device?.imei || device?._id}) • ${plate}`
                          : `${device?.name || "Unnamed"} (${device?.imei || device?._id})`;
                      }}
                      helperText={`Search ${summaryAvailableDevices.length} vehicles by name, IMEI, or plate`}
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12} sm={6} md={3}>
                  <SearchableDeviceSelect
                    label="Vehicle"
                    options={devicesList}
                    value={selectedDevice}
                    onChange={(device) => {
                      setSelectedImei(device ? getVehicleSelectValue(device) : "");
                      resetView();
                    }}
                    getOptionLabel={(device) => {
                      const plate = getDevicePlateLabel(device);
                      return plate
                        ? `${device?.name || "Unnamed"} (${device?.imei || device?._id}) • ${plate}`
                        : `${device?.name || "Unnamed"} (${device?.imei || device?._id})`;
                    }}
                    helperText={devicesList.length ? "" : "No devices available"}
                  />
                </Grid>
              )}

              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Time Range</InputLabel>
                  <Select
                    value={timeRange}
                    label="Time Range"
                    MenuProps={SELECT_MENU_PROPS}
                    onChange={(e) => {
                      setTimeRange(e.target.value);
                      resetView();
                    }}
                  >
                    {TIME_RANGES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {timeRange === "Custom" ? (
                <>
                  <Grid item xs={12} sm={3} md={2}>
                    <TextField
                      size="small"
                      label="Start Date"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={customStartDate}
                      onChange={(e) => {
                        setCustomStartDate(e.target.value);
                        resetView();
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3} md={2}>
                    <TextField
                      size="small"
                      label="End Date"
                      type="date"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={customEndDate}
                      onChange={(e) => {
                        setCustomEndDate(e.target.value);
                        resetView();
                      }}
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12} sm={6} md={4} />
              )}

              {reportType === "Overspeed Report" && !isDataPushMode && (
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    size="small"
                    label="Speed Limit"
                    type="number"
                    fullWidth
                    value={speedLimit}
                    onChange={(e) => setSpeedLimit(Number(e.target.value))}
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", md: "center" }}
                  flexWrap="wrap"
                  gap={1}
                >

                  <Box
                    display="flex"
                    gap={1}
                    flexWrap="wrap"
                    justifyContent={{ xs: "stretch", md: "flex-end" }}
                    width={{ xs: "100%", md: "auto" }}
                  >
                    <Button
                      variant="contained"
                      startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                      onClick={handleGenerate}
                      disabled={loading || (!isGroupSummaryReport && !selectedImei && !isVoltageReport && !isManagedTripReport && !isExpenseReport)}
                      sx={{ flex: { xs: "1 1 160px", md: "0 0 auto" } }}
                    >
                      Generate
                    </Button>

                    {isDataPushMode ? (
                      <>
                        <Button
                          variant="outlined"
                          startIcon={downloadBusy ? <CircularProgress size={16} /> : <DownloadIcon />}
                          onClick={() => handleDownload("csv")}
                          disabled={!selectedImei || loading || downloadBusy || !misMeta}
                          sx={{ flex: { xs: "1 1 120px", md: "0 0 auto" } }}
                        >
                          CSV
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={downloadBusy ? <CircularProgress size={16} /> : <DownloadIcon />}
                          onClick={() => handleDownload("json")}
                          disabled={!selectedImei || loading || downloadBusy || !misMeta}
                          sx={{ flex: { xs: "1 1 120px", md: "0 0 auto" } }}
                        >
                          JSON
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outlined"
                          startIcon={downloadBusy ? <CircularProgress size={16} /> : <DownloadIcon />}
                          onClick={() => handleDownload("excel")}
                          disabled={loading || downloadBusy || (!isGroupSummaryReport && !selectedImei && !isVoltageReport && !isManagedTripReport && !isExpenseReport)}
                          sx={{ flex: { xs: "1 1 120px", md: "0 0 auto" } }}
                        >
                          Excel
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={downloadBusy ? <CircularProgress size={16} /> : <DownloadIcon />}
                          onClick={() => handleDownload("pdf")}
                          disabled={loading || downloadBusy || (!isGroupSummaryReport && !selectedImei && !isVoltageReport && !isManagedTripReport && !isExpenseReport)}
                          sx={{ flex: { xs: "1 1 120px", md: "0 0 auto" } }}
                        >
                          PDF
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
              </Grid>

              {showFilters && !isDataPushMode && (
                <Grid item xs={12}>
                  <Box mt={2} p={2} border={1} borderColor="divider" borderRadius={1}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Advanced Filters
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box>
                          <Typography variant="caption" display="block" gutterBottom>
                            Minimum Time Gap: {minTimeGap} minutes
                          </Typography>
                          <Slider
                            value={minTimeGap}
                            onChange={(_, value) => setMinTimeGap(value)}
                            valueLabelDisplay="auto"
                            min={0}
                            max={60}
                            step={1}
                          />
                          <Box display="flex" justifyContent="space-between" mt={0.5}>
                            <Button size="small" onClick={() => setMinTimeGap(1)}>1 min</Button>
                            <Button size="small" onClick={() => setMinTimeGap(5)}>5 min</Button>
                            <Button size="small" onClick={() => setMinTimeGap(10)}>10 min</Button>
                            <Button size="small" onClick={() => setMinTimeGap(30)}>30 min</Button>
                            <Button size="small" onClick={() => setMinTimeGap(0)}>Clear</Button>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Preview Table */}
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                {isDataPushMode ? "Data Push Logs" : "Report Preview"}
                {filteredRows.length > 0 && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({filteredRows.length} records)
                  </Typography>
                )}
              </Typography>

              <Box display="flex" gap={1} alignItems="center">
                <TextField
                  size="small"
                  placeholder="Search in results..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  sx={{ width: 300 }}
                />
                <Button
                  variant="outlined"
                  onClick={handleDownloadPreviewCsv}
                  disabled={!filteredRows.length}
                >
                  Download CSV
                </Button>
              </Box>
            </Box>
            {!isDataPushMode && reportType === "Chart" && Array.isArray(previewRows) && previewRows.length > 0 && (
  <Box sx={{ height: 280, mb: 2 }}>
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={previewRows}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="time" hide />
        <YAxis />
        <RTooltip />
        <Legend />
        <Line type="monotone" dataKey="speed" dot={false} />
        <Line type="monotone" dataKey="distance" dot={false} />
        <Line type="monotone" dataKey="fuel" dot={false} />
        <Line type="monotone" dataKey="temperature" dot={false} />
      </LineChart>
    </ResponsiveContainer>
  </Box>
)}


            <Divider sx={{ mb: 2 }} />

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>{generationStatusLabel}</Typography>
              </Box>
            ) : (!selectedImei && !isGroupSummaryReport && !isVoltageReport && !isManagedTripReport && !isExpenseReport) ? (
              <Box textAlign="center" py={4}>
                <Typography color="text.secondary">
                  Select a vehicle to generate report
                </Typography>
              </Box>
            ) : isDataPushMode ? (
              !misMeta ? (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondground">
                    Click Generate to load Data Push logs
                  </Typography>
                </Box>
              ) : filteredRows.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography color="text.secondary">
                    No logs found
                  </Typography>
                </Box>
              ) : (
                <>
                  <ScrollableTableContainer sx={{ maxHeight: 500 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 900 }}>Time</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>IMEI</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>Integration</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>Endpoint</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>Response Code</TableCell>
                          <TableCell sx={{ fontWeight: 900 }}>Error</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                          <TableRow key={row?._id || idx} hover>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {row?.time ? formatTime(row.time) : "N/A"}
                            </TableCell>
                            <TableCell>{row?.imei || selectedDevice?.imei || "N/A"}</TableCell>
                            <TableCell>{row?.integrationName || "MoveInSync"}</TableCell>
                            <TableCell>
                              {row?.endpoint ? (
                                <Tooltip title={row.endpoint}>
                                  <Typography sx={{ 
                                    maxWidth: 200,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {row.endpoint}
                                  </Typography>
                                </Tooltip>
                              ) : "N/A"}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                size="small" 
                                color={row?.success ? "success" : "error"} 
                                label={row?.success ? "Success" : "Failed"} 
                              />
                            </TableCell>
                            <TableCell>{row?.responseCode || "N/A"}</TableCell>
                            <TableCell sx={{ color: row?.error ? 'error.main' : 'text.secondary' }}>
                              {row?.error || "N/A"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollableTableContainer>
                  
                  <TablePagination
                    component="div"
                    count={filteredRows.length}
                    page={page}
                    ActionsComponent={() => null}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                  />
                </>
              )
           ) : (
  !rawPreviewJson ? (
    <Box textAlign="center" py={4}>
      <Typography color="text.secondary">Click Generate to preview report</Typography>
    </Box>
  ) : filteredRows.length === 0 ? (
    <Box textAlign="center" py={4}>
      <Typography color="text.secondary">No data found</Typography>
    </Box>
  ) : isChartMode ? (
    // ✅ Chart mode => NO TABLE
    <Box textAlign="center" py={2}>
      <Typography color="text.secondary">
        Showing chart preview above
      </Typography>
    </Box>
  ) : (
    // ✅ Normal reports => TABLE
    <>
      <ScrollableTableContainer sx={{ maxHeight: 500 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          {tableColumns.map((col) => (
                            <TableCell key={col.key} sx={{ fontWeight: 900, whiteSpace: "nowrap" }}>
                              {col.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, idx) => (
                          <TableRow key={idx} hover>
                {tableColumns.map((col) => {
                  const value = row[col.key];

                  if (col.key === "address") {
                    return (
                      <TableCell key={col.key}>
                        <Tooltip title={value || "No address"}>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <LocationOnIcon fontSize="small" color="primary" />
                            <Typography sx={{ maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {value || "No Location"}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                    );
                  }

                  const isTimeField =
                    col.key.toLowerCase().includes("time") ||
                    col.key.toLowerCase().includes("date") ||
                    col.key === "duration" ||
                    col.key === "timeGapDisplay";

                  if (isTimeField) {
                    return (
                      <TableCell key={col.key} sx={{ whiteSpace: "nowrap" }}>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <AccessTimeIcon fontSize="small" color="action" />
                          <Typography>{formatCellValue(value)}</Typography>
                        </Box>
                      </TableCell>
                    );
                  }

                  const display =
                    col.key === "temperature" && (value === 0 || value === "0")
                      ? ""
                      : formatCellValue(value);
                  return (
                    <TableCell key={col.key} sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title={display}>
                        <Typography sx={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {display}
                                </Typography>
                              </Tooltip>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
      </ScrollableTableContainer>

      <TablePagination
        component="div"
        count={filteredRows.length}
        page={page}
        ActionsComponent={() => null}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </>
  )
)
}
          </Paper>

          {/* Raw JSON Viewer */}
          {(rawPreviewJson || misMeta) && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 900 }}>Raw JSON Data</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, overflow: "auto", maxHeight: 300 }}>
                  <pre style={{ margin: 0, fontSize: '0.8rem' }}>
                    {JSON.stringify(isDataPushMode ? misMeta : rawPreviewJson, null, 2)}
                  </pre>
                </Paper>
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
